/*
  Shared, browser-safe expectation rules for creator overview and progress UI.
  New variants use one link metric and one mention metric at most; legacy
  variants preserve existing opportunities that have both count and day targets.
*/

const _ = require('lodash');
const { getRequiredLinkCreationSourceLabels } = require('../link_creation_sources');

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EXPECTATION_VARIANT_IDS = {
  V1: 'V1', // links only
  V2: 'V2', // linkingDays only
  V3: 'V3', // mentions only
  V4: 'V4', // mentionDays only
  V5: 'V5', // links + mentions
  V6: 'V6', // links + mentionDays
  V7: 'V7', // linkingDays + mentions
  V8: 'V8', // linkingDays + mentionDays

  L1: 'L1', // links+linkingDays only (legacy)
  L2: 'L2', // mentions+mentionDays only (legacy)
  L3: 'L3', // links+linkingDays + some mention side (legacy)
  L4: 'L4' // links/linkingDays/none + mentions+mentionDays (legacy)
};

const EXPECTATION_CARD_KINDS = {
  UNIQUE_LINKS: 'unique-links',
  DAYS_WITH_LINKS: 'days-with-links',
  SOCIAL_MENTIONS: 'social-mentions',
  DAYS_WITH_MENTIONS: 'days-with-mentions'
};

const MIN_LINK_CLICKS = 10;
const MAX_DAYS_BEFORE_TRACKING_FOR_LINK_CREATION = 7;

const MENTION_PLATFORM_LABELS = {
  instagram: 'INSTAGRAM',
  tiktok: 'TIKTOK',
  youtube: 'YOUTUBE'
};

const ALL_MENTION_PLATFORM_KEYS = ['instagram', 'tiktok', 'youtube'];

// Shared social-mention education shown behind platform chips.
const PLATFORM_EDUCATION = {
  label: 'Platforms',
  tabs: [
    { key: 'instagram', label: 'Instagram', icon: 'instagram' },
    { key: 'tiktok', label: 'TikTok', icon: 'tiktok' },
    { key: 'youtube', label: 'YouTube', icon: 'youtube' }
  ],
  panels: {
    instagram: {
      key: 'instagram',
      icon: 'instagram',
      intro: 'Instagram mentions must include one of the brand’s accepted social mention tags.',
      sections: [
        {
          key: 'instagram-feed-post',
          title: 'Instagram Feed Post',
          bullets: [
            'Post a photo or carousel to your Instagram feed.',
            'Include any of the brand’s accepted social mention tags in the post caption.'
          ]
        },
        {
          key: 'instagram-reel',
          title: 'Instagram Reel',
          bullets: ['Post a Reel to Instagram.', 'Include any of the brand’s accepted social mention tags in the Reel caption.']
        },
        {
          key: 'instagram-story',
          title: 'Instagram Story',
          bullets: [
            'Post a Story that mentions the brand.',
            {
              text: 'Include any of the brand’s accepted social mention tags in one of the following ways:',
              subBullets: ['In the caption field - or -', 'As visible text overlay on the Story']
            }
          ]
        }
      ]
    },
    tiktok: {
      key: 'tiktok',
      icon: 'tiktok',
      intro: 'TikTok mentions must include one of the brand’s accepted social mention tags in the caption (the text below the video).',
      sections: [
        {
          key: 'tiktok-video',
          title: 'TikTok Video',
          bullets: ['Post a standard TikTok video.', 'Include any of the brand’s accepted social mention tags in the video caption.']
        },
        {
          key: 'tiktok-photo-post',
          title: 'TikTok Photo Post',
          bullets: ['Post a TikTok photo carousel.', 'Include any of the brand’s accepted social mention tags in the caption.']
        }
      ]
    },
    youtube: {
      key: 'youtube',
      icon: 'youtube',
      intro: 'YouTube mentions must include one of the brand’s accepted social mention tags in either the title or description.',
      sections: [
        {
          key: 'youtube-video',
          title: 'YouTube Video',
          bullets: ['Post a standard YouTube video.', 'Include any of the brand’s accepted social mention tags in the video title or description.']
        },
        {
          key: 'youtube-short',
          title: 'YouTube Short',
          bullets: ['Post a YouTube Short.', 'Include any of the brand’s accepted social mention tags in the title or description.']
        },
        {
          key: 'youtube-live',
          title: 'YouTube Live',
          bullets: ['Go live on YouTube.', 'Include any of the brand’s accepted social mention tags in the stream title or description.']
        }
      ]
    }
  }
};

// Small string helpers

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatPrettyDate = input => {
  if (!input) return null;
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  return `${MONTHS_LONG[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const pluralize = (n, singular, plural) => (Number(n) === 1 ? singular : plural || `${singular}s`);

const splitCsv = value => {
  if (!value) return [];
  if (Array.isArray(value)) return _.compact(value.map(v => (v == null ? '' : String(v).trim())));
  return _.compact(
    String(value)
      .split(',')
      .map(v => v.trim())
  );
};

const normalizeChipLabel = label => (label || '').toString();

// Chip group builders

const buildPlatformChips = mentionPlatformsArr => {
  // Empty mentionPlatforms means all supported platforms are accepted.
  const keys = mentionPlatformsArr.length ? mentionPlatformsArr : ALL_MENTION_PLATFORM_KEYS;
  return keys
    .map(rawKey => {
      const platformKey = String(rawKey).toLowerCase();
      const label = MENTION_PLATFORM_LABELS[platformKey];
      if (!label) return null;
      return { label, icon: platformKey, platformKey };
    })
    .filter(Boolean);
};

const buildTagChips = (socialTagsArr, brandSocialTagsArr) => {
  const tags = socialTagsArr.length ? socialTagsArr : brandSocialTagsArr;
  return tags.filter(tag => tag && !tag.includes(' ')).map(tag => ({ label: normalizeChipLabel(tag), icon: 'tag' }));
};

const buildDomainChips = acceptableDomainsArr =>
  acceptableDomainsArr.map(domain => ({ label: normalizeChipLabel(domain).toUpperCase(), icon: 'external-link' }));

// '*' opens the scope only for brands the stats job honors the wildcard for; anyone else's '*' and a
// blank list both fall back to the brand's own domain, since creators always need some domain to chase.
const resolveLinkDomains = ({ acceptableDomains, brand, canUseDomainWildcard = false, normalizeDomain = domain => domain }) => {
  const brandDomains = brand && brand.domain ? [brand.domain] : [];
  if (acceptableDomains === '*') return canUseDomainWildcard ? [] : brandDomains;
  const domains = _.uniq(
    splitCsv(acceptableDomains)
      .map(normalizeDomain)
      .filter(Boolean)
  );
  return domains.length ? domains : brandDomains;
};

const buildLinkChipGroups = ({ linkDomains }) => {
  if (!linkDomains.length) return [];
  return [{ key: 'domains', title: 'Domains', chips: buildDomainChips(linkDomains) }];
};

const buildMentionChipGroups = ({ mentionPlatformsArr, socialTagsArr, brandSocialTagsArr }) => {
  const groups = [];
  groups.push({ key: 'platforms', title: 'Platforms', chips: buildPlatformChips(mentionPlatformsArr) });

  const tagChips = buildTagChips(socialTagsArr, brandSocialTagsArr);
  if (tagChips.length) groups.push({ key: 'tags', title: 'Tags', chips: tagChips });

  return groups;
};

// Bullet builders

const trackingWindowBullet = ({ trackingStartsAt, trackingEndsAt, isMention }) => {
  const start = formatPrettyDate(trackingStartsAt);
  const end = formatPrettyDate(trackingEndsAt);
  if (!start || !end) return null;
  return isMention ? `Post the mentions between ${start} and ${end}` : `Share the links between ${start} and ${end}`;
};

// Only creators carry a personalized date (via getMinimumDateForLinkCreation); brands get no bullet.
// Late acceptances clamp to the tracking start, where the share-window bullet already names the date.
const linkCreationBullet = ({ minimumLinkCreationDate, trackingStartsAt }) => {
  const date = formatPrettyDate(minimumLinkCreationDate);
  if (!date || date === formatPrettyDate(trackingStartsAt)) return null;
  return `Links can be created from ${date}`;
};

const linkDomainsBullet = ({ linkDomains }) => {
  if (!linkDomains.length) return null;
  if (linkDomains.length > 1) return 'The links must be to any of the listed acceptable domains';
  return `Links must be to ${linkDomains[0]}`;
};

const onlyCountQuickLinksBullet = ({ onlyCountQuickLinks }) =>
  onlyCountQuickLinks ? 'Only links not in a collection (Quick Links) will count toward this expectation' : null;

const joinRequiredSourceLabels = requiredSourceLabelsArr => requiredSourceLabelsArr.join(' or the ');

// "Mobile Safari Extension " (trailing space) folds the required source labels into
// the "Unique {source} Links" copy shared by the creator cards and the brand
// performance list; empty when no source is required.
const buildRequiredSourceQualifier = requiredSourceLabelsArr =>
  requiredSourceLabelsArr.length ? `${requiredSourceLabelsArr.join(' or ')} ` : '';

// "Links must be created via the Mobile Safari Extension." shared by the creator
// cards and the legacy opportunity/progress screens; empty when none is required.
const buildRequiredSourceSentence = requiredSourceLabelsArr =>
  requiredSourceLabelsArr.length ? `Links must be created via the ${joinRequiredSourceLabels(requiredSourceLabelsArr)}.` : '';

const requiredSourcesBullet = ({ requiredSourceLabelsArr }) =>
  requiredSourceLabelsArr.length ? `Links must be created via the ${joinRequiredSourceLabels(requiredSourceLabelsArr)}` : null;

const requiredSourcesSentence = ({ requiredSourceLabelsArr }) => {
  const sentence = buildRequiredSourceSentence(requiredSourceLabelsArr);
  return sentence ? ` ${sentence}` : '';
};

const mentionGapBullet = ({ overrideMinutesBetweenStoriesToCountAsOne }) => {
  const minutes = Number(overrideMinutesBetweenStoriesToCountAsOne);
  if (!minutes || minutes <= 0) return null;
  return `Each mention needs over ${minutes} minutes gap from the previous mention to count as separate`;
};

const buildLinksBullets = ({ count, ctx }) => {
  const bullets = [`Generate and share ${count} unique ShopMy ${pluralize(count, 'link')}`];
  const creation = linkCreationBullet(ctx);
  if (creation) bullets.push(creation);
  const window = trackingWindowBullet({ ...ctx, isMention: false });
  if (window) bullets.push(window);
  bullets.push(`Links must receive at least ${MIN_LINK_CLICKS} clicks to qualify`);
  const domainBullet = linkDomainsBullet(ctx);
  if (domainBullet) bullets.push(domainBullet);
  const sourceBullet = requiredSourcesBullet(ctx);
  if (sourceBullet) bullets.push(sourceBullet);
  const quickLinks = onlyCountQuickLinksBullet(ctx);
  if (quickLinks) bullets.push(quickLinks);
  return bullets;
};

const buildLinkingDaysBullets = ({ count, ctx }) => {
  const linkLabel = ctx.onlyCountQuickLinks ? 'Quick Link' : 'ShopMy link';
  const linkLabelPlural = ctx.onlyCountQuickLinks ? 'Quick Links' : 'ShopMy links';
  const bullets = [count === 1 ? `Share a ${linkLabel} on at least 1 day` : `Share ${linkLabelPlural} across ${count} separate ${pluralize(count, 'day')}`];
  const creation = linkCreationBullet(ctx);
  if (creation) bullets.push(creation);
  const window = trackingWindowBullet({ ...ctx, isMention: false });
  if (window) bullets.push(window);
  bullets.push(`Each day must drive at least ${MIN_LINK_CLICKS} clicks for a link to count`);
  const domainBullet = linkDomainsBullet(ctx);
  if (domainBullet) bullets.push(domainBullet);
  const sourceBullet = requiredSourcesBullet(ctx);
  if (sourceBullet) bullets.push(sourceBullet);
  const quickLinks = onlyCountQuickLinksBullet(ctx);
  if (quickLinks) bullets.push(quickLinks);
  return bullets;
};

const buildMentionsBullets = ({ count, ctx }) => {
  const brandName = ctx.brand?.name;
  const bullets = [
    brandName ? `Mention ${brandName} at least ${count} ${pluralize(count, 'time')}` : `Make ${count} social ${pluralize(count, 'mention')}`
  ];
  const window = trackingWindowBullet({ ...ctx, isMention: true });
  if (window) bullets.push(window);
  bullets.push('The social tag must be visible on your mention. Hidden tags will not count');
  const gap = mentionGapBullet(ctx);
  if (gap) bullets.push(gap);
  return bullets;
};

const buildMentionDaysBullets = ({ count, ctx }) => {
  const bullets = [
    count === 1 ? 'Make a social mention on at least 1 day' : `Make a social mention across ${count} separate ${pluralize(count, 'day')}`
  ];
  const window = trackingWindowBullet({ ...ctx, isMention: true });
  if (window) bullets.push(window);
  bullets.push('The social tag must be visible on your mention. Hidden tags will not count');
  const gap = mentionGapBullet(ctx);
  if (gap) bullets.push(gap);
  return bullets;
};

// Card builders

const buildLinksCard = ({ linksExpected, ctx }) => {
  const count = +linksExpected;
  const linkLabel = ctx.onlyCountQuickLinks ? 'Quick Link' : 'Link';
  const linkLabelPlural = pluralize(count, linkLabel);
  // "2 Unique Mobile Safari Extension Links" when a creation source is required.
  const sourceQualifier = buildRequiredSourceQualifier(ctx.requiredSourceLabelsArr);
  return {
    key: 'links',
    kind: EXPECTATION_CARD_KINDS.UNIQUE_LINKS,
    count,
    title: `${count} Unique ${sourceQualifier}${linkLabelPlural}`,
    shortTitle: `UNIQUE ${sourceQualifier.toUpperCase()}${linkLabelPlural.toUpperCase()}`,
    bullets: buildLinksBullets({ count, ctx }),
    chipGroups: buildLinkChipGroups(ctx),
    footnotes: ctx.onlyCountQuickLinks ? ['Links associated with collections will not count.'] : [],
    taskDescription: `Share ${count} unique ${pluralize(
      count,
      linkLabel
    )} for this opportunity. Each link must drive at least ${MIN_LINK_CLICKS} clicks to qualify.${requiredSourcesSentence(ctx)}`
  };
};

const buildLinkingDaysCard = ({ linkingDaysExpected, ctx }) => {
  const count = +linkingDaysExpected;
  const linkLabel = ctx.onlyCountQuickLinks ? 'Quick Link' : 'Link';
  const linkLabelLower = ctx.onlyCountQuickLinks ? 'Quick Link' : 'ShopMy link';
  return {
    key: 'linkingDays',
    kind: EXPECTATION_CARD_KINDS.DAYS_WITH_LINKS,
    count,
    title: `${pluralize(count, linkLabel)} on ${count} Separate ${pluralize(count, 'Day')}`,
    shortTitle: `${pluralize(count, linkLabel).toUpperCase()} ON ${count} SEPARATE ${pluralize(count, 'DAY')}`,
    bullets: buildLinkingDaysBullets({ count, ctx }),
    chipGroups: buildLinkChipGroups(ctx),
    footnotes: ctx.onlyCountQuickLinks ? ['Links associated with collections will not count.'] : [],
    taskDescription: `Share at least one ${linkLabelLower} on ${count} separate ${pluralize(
      count,
      'day'
    )}. Each day's link must drive at least ${MIN_LINK_CLICKS} clicks to qualify.${requiredSourcesSentence(ctx)}`
  };
};

const buildMentionsCard = ({ mentionsExpected, ctx }) => {
  const count = +mentionsExpected;
  return {
    key: 'mentions',
    kind: EXPECTATION_CARD_KINDS.SOCIAL_MENTIONS,
    count,
    title: `${count} Social ${pluralize(count, 'Mention')}`,
    shortTitle: `SOCIAL ${pluralize(count, 'MENTION')}`,
    bullets: buildMentionsBullets({ count, ctx }),
    chipGroups: buildMentionChipGroups(ctx),
    platformEducation: PLATFORM_EDUCATION,
    footnotes: [],
    taskDescription: `Make ${count} social ${pluralize(count, 'mention')} on the listed platforms using one of the accepted tags.`
  };
};

const buildMentionDaysCard = ({ mentionDaysExpected, ctx }) => {
  const count = +mentionDaysExpected;
  return {
    key: 'mentionDays',
    kind: EXPECTATION_CARD_KINDS.DAYS_WITH_MENTIONS,
    count,
    title: `Mentions on ${count} Separate ${pluralize(count, 'Day')}`,
    shortTitle: `MENTIONS ON ${count} ${pluralize(count, 'DAY')}`,
    bullets: buildMentionDaysBullets({ count, ctx }),
    chipGroups: buildMentionChipGroups(ctx),
    platformEducation: PLATFORM_EDUCATION,
    footnotes: [],
    taskDescription: `Make a social mention on ${count} separate ${pluralize(count, 'day')} using one of the accepted tags.`
  };
};

// Detection

const determineLinkPiece = (linksExpected, linkingDaysExpected) => {
  const hasLinks = +linksExpected > 0;
  const hasLinkingDays = +linkingDaysExpected > 0;
  if (hasLinks && hasLinkingDays) return 'links+linkingDays';
  if (hasLinks) return 'links';
  if (hasLinkingDays) return 'linkingDays';
  return 'none';
};

const determineMentionPiece = (mentionsExpected, mentionDaysExpected) => {
  const hasMentions = +mentionsExpected > 0;
  const hasMentionDays = +mentionDaysExpected > 0;
  if (hasMentions && hasMentionDays) return 'mentions+mentionDays';
  if (hasMentions) return 'mentions';
  if (hasMentionDays) return 'mentionDays';
  return 'none';
};

const determineVariantId = (linkPiece, mentionPiece) => {
  const linkLegacy = linkPiece === 'links+linkingDays';
  const mentionLegacy = mentionPiece === 'mentions+mentionDays';

  // L3 covers link-side legacy, including the rare case where both sides are legacy.
  if (linkLegacy && mentionPiece === 'none') return EXPECTATION_VARIANT_IDS.L1;
  if (mentionLegacy && linkPiece === 'none') return EXPECTATION_VARIANT_IDS.L2;
  if (linkLegacy) return EXPECTATION_VARIANT_IDS.L3;
  if (mentionLegacy) return EXPECTATION_VARIANT_IDS.L4;

  // V1..V8 clean combinations
  if (linkPiece === 'links' && mentionPiece === 'none') return EXPECTATION_VARIANT_IDS.V1;
  if (linkPiece === 'linkingDays' && mentionPiece === 'none') return EXPECTATION_VARIANT_IDS.V2;
  if (linkPiece === 'none' && mentionPiece === 'mentions') return EXPECTATION_VARIANT_IDS.V3;
  if (linkPiece === 'none' && mentionPiece === 'mentionDays') return EXPECTATION_VARIANT_IDS.V4;
  if (linkPiece === 'links' && mentionPiece === 'mentions') return EXPECTATION_VARIANT_IDS.V5;
  if (linkPiece === 'links' && mentionPiece === 'mentionDays') return EXPECTATION_VARIANT_IDS.V6;
  if (linkPiece === 'linkingDays' && mentionPiece === 'mentions') return EXPECTATION_VARIANT_IDS.V7;
  if (linkPiece === 'linkingDays' && mentionPiece === 'mentionDays') return EXPECTATION_VARIANT_IDS.V8;

  return null; // No expectations at all
};

// Public API

/**
 * Build the cards array for an opportunity, given a normalized input set.
 *
 * @param {object} input
 * @param {number} input.linksExpected
 * @param {number} input.linkingDaysExpected
 * @param {number} input.mentionsExpected
 * @param {number} input.mentionDaysExpected
 * @param {string[]=} input.linkDomains        Domains the links must point at, resolved via resolveLinkDomains; empty leaves them unscoped
 * @param {string=} input.requiredLinkCreationSources Comma-separated link_creation_sources tokens
 * @param {string=} input.mentionPlatforms     Comma-separated platform keys
 * @param {string=} input.socialTags           Comma-separated tags
 * @param {boolean=} input.onlyCountQuickLinks Adds the Quick-Link bullet
 * @param {number=} input.overrideMinutesBetweenStoriesToCountAsOne
 * @param {string|Date=} input.trackingStartsAt
 * @param {string|Date=} input.trackingEndsAt
 * @param {string|Date=} input.minimumLinkCreationDate Personalized creator
 *   "earliest link date"; adds a "Links can be created from" bullet to link cards.
 * @param {{ name?: string, socialTags?: string }=} input.brand
 *   Fallback for socialTags; the name feeds the mention bullets.
 * @returns {{ variantId: string|null, isLegacy: boolean, cards: object[] }}
 */
const getExpectationVariant = (input = {}) => {
  const {
    linksExpected = 0,
    linkingDaysExpected = 0,
    mentionsExpected = 0,
    mentionDaysExpected = 0,
    linkDomains = [],
    requiredLinkCreationSources = '',
    mentionPlatforms = '',
    socialTags = '',
    onlyCountQuickLinks = false,
    overrideMinutesBetweenStoriesToCountAsOne = 0,
    trackingStartsAt = null,
    trackingEndsAt = null,
    minimumLinkCreationDate = null,
    brand = null
  } = input;

  const linkPiece = determineLinkPiece(linksExpected, linkingDaysExpected);
  const mentionPiece = determineMentionPiece(mentionsExpected, mentionDaysExpected);
  const variantId = determineVariantId(linkPiece, mentionPiece);
  const isLegacy = variantId === 'L1' || variantId === 'L2' || variantId === 'L3' || variantId === 'L4';

  const ctx = {
    linkDomains: Array.isArray(linkDomains) ? linkDomains : [],
    requiredSourceLabelsArr: getRequiredLinkCreationSourceLabels(requiredLinkCreationSources),
    mentionPlatformsArr: splitCsv(mentionPlatforms).map(s => s.toLowerCase()),
    socialTagsArr: splitCsv(socialTags),
    brandSocialTagsArr: splitCsv(brand?.socialTags),
    onlyCountQuickLinks: !!onlyCountQuickLinks,
    overrideMinutesBetweenStoriesToCountAsOne: +overrideMinutesBetweenStoriesToCountAsOne || 0,
    trackingStartsAt,
    trackingEndsAt,
    minimumLinkCreationDate: minimumLinkCreationDate || null,
    brand: brand || null
  };

  // Legacy variants (`links+linkingDays`, `mentions+mentionDays`) emit BOTH
  // sub-cards instead of a combined card. This restores per-metric progress
  // counts and matches the legacy progress UI's separate sections.
  const cards = [];

  if (linkPiece === 'links') cards.push(buildLinksCard({ linksExpected, ctx }));
  else if (linkPiece === 'linkingDays') cards.push(buildLinkingDaysCard({ linkingDaysExpected, ctx }));
  else if (linkPiece === 'links+linkingDays') {
    cards.push(buildLinksCard({ linksExpected, ctx }));
    cards.push(buildLinkingDaysCard({ linkingDaysExpected, ctx }));
  }

  if (mentionPiece === 'mentions') cards.push(buildMentionsCard({ mentionsExpected, ctx }));
  else if (mentionPiece === 'mentionDays') cards.push(buildMentionDaysCard({ mentionDaysExpected, ctx }));
  else if (mentionPiece === 'mentions+mentionDays') {
    cards.push(buildMentionsCard({ mentionsExpected, ctx }));
    cards.push(buildMentionDaysCard({ mentionDaysExpected, ctx }));
  }

  return { variantId, isLegacy, cards };
};

const buildExpectationVariantCards = input => getExpectationVariant(input).cards;

module.exports = {
  EXPECTATION_VARIANT_IDS,
  EXPECTATION_CARD_KINDS,
  PLATFORM_EDUCATION,
  MIN_LINK_CLICKS,
  MAX_DAYS_BEFORE_TRACKING_FOR_LINK_CREATION,
  getExpectationVariant,
  buildExpectationVariantCards,
  buildRequiredSourceQualifier,
  buildRequiredSourceSentence,
  resolveLinkDomains
};
